// src/modules/app-views/fiscal-config/pages/Document/tabs/CertificateTab/CertificateCard.jsx
import { useMemo } from 'react';
import { Card, Button, Descriptions, Tag, Space, Alert, Progress, Tooltip, Divider } from 'antd';
import {
  SafetyCertificateOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';
import { parseCnpjToDisplay } from 'utils/helpers';
import { getProgressColor } from './constants';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CertificateCard = ({ displayCertificate, isMobile, locked, onUpload, onDelete }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────

  // Clean holder name (remove CNPJ/CPF suffix if present)
  // Brazilian certificates format: "COMPANY NAME:CNPJ" or "PERSON NAME:CPF"
  const cleanHolderName = useMemo(() => {
    const holderName = displayCertificate?.holderName;
    if (!holderName || !holderName.includes(':')) return holderName;

    const parts = holderName.split(':');
    const lastPart = parts[parts.length - 1].trim();
    const cleanDoc = lastPart.replace(/\D/g, '');

    // Check if last part is a CNPJ (14 digits) or CPF (11 digits)
    if (cleanDoc.length === 14 || cleanDoc.length === 11) {
      return parts.slice(0, -1).join(':').trim();
    }

    return holderName;
  }, [displayCertificate?.holderName]);

  const certificateStatus = useMemo(() => {
    // Handle both expiresAt (from upload) and validUntil (from aggregate)
    const expiryDate = displayCertificate?.expiresAt || displayCertificate?.validUntil;

    if (!expiryDate) {
      return {
        status: 'none',
        color: 'default',
        icon: null,
        text: 'not_configured',
        progressPercent: 0,
        daysUntilExpiry: 0,
      };
    }

    const validFrom = dayjs(displayCertificate.validFrom);
    const expiresAt = dayjs(expiryDate);
    const now = dayjs();

    const totalDays = expiresAt.diff(validFrom, 'day');
    const elapsedDays = now.diff(validFrom, 'day');
    const daysUntilExpiry = displayCertificate.daysUntilExpiry ?? expiresAt.diff(now, 'day');

    // Progress percentage (0% = just created, 100% = expired)
    const progressPercent = Math.max(0, Math.min(100, (elapsedDays / totalDays) * 100));

    if (daysUntilExpiry < 0) {
      return {
        status: 'expired',
        color: 'error',
        icon: <CloseCircleOutlined />,
        text: 'expired',
        progressPercent,
        daysUntilExpiry: 0,
      };
    }
    if (daysUntilExpiry <= 30) {
      return {
        status: 'expiring',
        color: 'warning',
        icon: <WarningOutlined />,
        text: 'expiring_soon',
        progressPercent,
        daysUntilExpiry,
      };
    }
    return {
      status: 'valid',
      color: 'success',
      icon: <CheckCircleOutlined />,
      text: 'valid',
      progressPercent,
      daysUntilExpiry,
    };
  }, [displayCertificate]);

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Card
      title={
        <Space>
          <SafetyCertificateOutlined />
          <span className="text-muted">{translateX('digital_certificate')}</span>
        </Space>
      }
      extra={
        // Only show buttons in header on desktop
        !isMobile && (
          <Space>
            {displayCertificate && (
              <Tooltip title={translateX('remove_certificate')}>
                <Button
                  type="default"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={onDelete}
                  disabled={locked}
                />
              </Tooltip>
            )}
            <Button type="default" icon={<UploadOutlined />} onClick={onUpload} disabled={locked}>
              {displayCertificate
                ? translateX('update_certificate')
                : translateX('upload_certificate')}
            </Button>
          </Space>
        )
      }
    >
      {/* Mobile: show buttons below title */}
      {isMobile && (
        <div style={{ marginBottom: 36, display: 'flex', justifyContent: 'center', gap: 8 }}>
          {displayCertificate && (
            <Tooltip title={translateX('remove_certificate')}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                onClick={onDelete}
                disabled={locked}
              />
            </Tooltip>
          )}
          <Button type="primary" icon={<UploadOutlined />} onClick={onUpload} disabled={locked}>
            {displayCertificate
              ? translateX('update_certificate')
              : translateX('upload_certificate')}
          </Button>
        </div>
      )}
      {displayCertificate ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="middle">
          {/* Expiry Progress Bar */}
          <div style={{ padding: '0 8px' }}>
            <Tooltip title={translateX('days_until_expiry')}>
              <Progress
                percent={certificateStatus.progressPercent}
                format={() => (
                  <Space size={4}>
                    {certificateStatus.icon}
                    <span>
                      {certificateStatus.daysUntilExpiry} {translateX('days')}
                    </span>
                  </Space>
                )}
                strokeColor={getProgressColor(certificateStatus.progressPercent)}
                size={['100%', 16]}
              />
            </Tooltip>
          </div>

          <Divider style={{ margin: '8px 0' }} />

          {/* Certificate Details */}
          <Descriptions
            column={1}
            size="small"
            bordered
            styles={{ label: { width: 80, fontWeight: 500, textAlign: 'left' } }}
          >
            <Descriptions.Item label={translateX('holder')}>
              {cleanHolderName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="CNPJ">
              {displayCertificate?.cnpj ? parseCnpjToDisplay(displayCertificate.cnpj) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translateX('serial')}>
              {displayCertificate.serialNumber || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translateX('issuance')}>
              {displayCertificate.validFrom
                ? dayjs(displayCertificate.validFrom).format('DD/MM/YYYY HH:mm')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={translateX('expiration')}>
              <Space>
                {displayCertificate.expiresAt || displayCertificate.validUntil
                  ? dayjs(displayCertificate.expiresAt || displayCertificate.validUntil).format(
                      'DD/MM/YYYY HH:mm'
                    )
                  : '-'}
                <Tag color={certificateStatus.color}>
                  {certificateStatus.daysUntilExpiry} {translateX('day_s')}
                </Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={translateX('issuer')}>
              {displayCertificate.issuer || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Space>
      ) : (
        <Alert
          title={translateX('no_certificate_configured')}
          description={translateX('no_certificate_configured_description')}
          type="warning"
          showIcon
        />
      )}
    </Card>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CertificateCard;
